import { motion } from "framer-motion";
import { AlertOctagon, Check, Sparkles, X } from "lucide-react";

const PHASE_STYLES = {
    verification: { label: "Vérification", color: "bg-blue-50 text-blue-700 border-blue-200" },
    discovery: { label: "Découverte", color: "bg-shariti-orange-soft text-shariti-orange border-shariti-orange/30" },
    mental: { label: "Mental", color: "bg-violet-50 text-violet-700 border-violet-200" },
    projection: { label: "Projection", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    observation: { label: "Observation", color: "bg-slate-100 text-slate-600 border-slate-200" },
};

const NOTE_COLOR = (n, active) => {
    if (!active) return "bg-white text-slate-500 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange";
    if (n <= 2) return "bg-shariti-red text-white border-shariti-red shadow-sm";
    if (n === 3) return "bg-shariti-yellow text-shariti-black border-shariti-yellow";
    return "bg-shariti-green text-white border-shariti-green shadow-sm";
};

/**
 * One question of the unified pre-recruitment script.
 *
 * Props:
 *   - item: ENTRETIEN_SCRIPT entry
 *   - number: integer (1-based)
 *   - state: { answer?: "yes"|"no", note?: 1..5, comment?: string }
 *   - onUpdate(partial): patch the state
 *   - gatingFail: bool — flag when this gating item is failed
 */
export default function EntretienItem({ item, number, state = {}, onUpdate, gatingFail = false }) {
    const phaseStyle = PHASE_STYLES[item.phase] || PHASE_STYLES.discovery;
    const isGating = item.kind === "yesno_gating" || item.kind === "rating_gating";
    const showRating = item.kind === "rating" || item.kind === "rating_gating";
    const showYesNo = item.kind === "yesno_gating" || item.kind === "yesno_text";
    const showText = item.kind === "text" || item.kind === "yesno_text";
    const showCommentByDefault = showRating || item.with_comment === true;

    return (
        <motion.div
            data-testid={`entretien-item-${item.id}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-2xl border p-4 md:p-5 transition ${
                gatingFail
                    ? "border-red-300 bg-red-50/40"
                    : "border-shariti-gray bg-white"
            }`}
        >
            <div className="flex items-start gap-3">
                <div
                    className={`shrink-0 h-10 min-w-10 px-1.5 rounded-xl flex items-center justify-center text-sm font-black tracking-tight ${
                        isGating ? "bg-amber-100 text-amber-800" : "bg-shariti-orange-soft text-shariti-orange"
                    }`}
                >
                    Q{number}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${phaseStyle.color}`}>
                            {phaseStyle.label}
                        </span>
                        {isGating && (
                            <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                Éliminatoire
                            </span>
                        )}
                        {item.criterion_ids?.length > 0 && (
                            <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-shariti-cream text-slate-500 border border-shariti-gray inline-flex items-center gap-1">
                                <Sparkles className="h-2.5 w-2.5 text-shariti-orange" />
                                Score auto
                            </span>
                        )}
                    </div>
                    <h4 className={`text-sm md:text-base font-extrabold leading-snug ${item.no_speak ? "italic text-slate-600" : "text-shariti-black"}`}>
                        {item.q}
                    </h4>
                    {item.hint && (
                        <p className="mt-1 text-[11px] font-medium text-slate-500">{item.hint}</p>
                    )}
                    {item.criterion_hint && showRating && (
                        <p className="mt-1 text-[11px] font-medium text-shariti-orange/80">
                            {item.criterion_hint}
                        </p>
                    )}
                </div>
            </div>

            {/* Yes/No */}
            {showYesNo && (
                <div className="mt-3 ml-13 pl-0 md:pl-13 flex items-center gap-2">
                    <button
                        type="button"
                        data-testid={`entretien-${item.id}-yes`}
                        onClick={() => onUpdate({ answer: state.answer === "yes" ? null : "yes" })}
                        className={`h-9 px-4 rounded-full text-xs font-extrabold transition border-2 inline-flex items-center gap-1 ${
                            state.answer === "yes"
                                ? "bg-shariti-green text-white border-shariti-green"
                                : "bg-white text-slate-500 border-shariti-gray hover:border-shariti-green hover:text-shariti-green"
                        }`}
                    >
                        <Check className="h-3.5 w-3.5" /> Oui
                    </button>
                    <button
                        type="button"
                        data-testid={`entretien-${item.id}-no`}
                        onClick={() => onUpdate({ answer: state.answer === "no" ? null : "no" })}
                        className={`h-9 px-4 rounded-full text-xs font-extrabold transition border-2 inline-flex items-center gap-1 ${
                            state.answer === "no"
                                ? "bg-shariti-red text-white border-shariti-red"
                                : "bg-white text-slate-500 border-shariti-gray hover:border-shariti-red hover:text-shariti-red"
                        }`}
                    >
                        <X className="h-3.5 w-3.5" /> Non
                    </button>
                </div>
            )}

            {/* Rating 1-5 */}
            {showRating && (
                <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                    {[1, 2, 3, 4, 5].map((n) => {
                        const active = state.note === n;
                        return (
                            <button
                                key={n}
                                type="button"
                                data-testid={`entretien-${item.id}-note-${n}`}
                                onClick={() => onUpdate({ note: active ? undefined : n })}
                                className={`h-9 w-9 rounded-xl text-sm font-extrabold transition active:scale-95 border-2 ${NOTE_COLOR(n, active)}`}
                            >
                                {n}
                            </button>
                        );
                    })}
                    {state.note && (
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            {state.note <= 2 ? "Faible" : state.note === 3 ? "Moyen" : "Fort"}
                        </span>
                    )}
                </div>
            )}

            {/* Comment / text response */}
            {(showText || showCommentByDefault) && (
                <textarea
                    value={state.comment || ""}
                    onChange={(e) => onUpdate({ comment: e.target.value })}
                    rows={showText ? 2 : 1}
                    data-testid={`entretien-${item.id}-comment`}
                    placeholder={
                        showText ? "Réponse du candidat / notes" : "Commentaire (optionnel)"
                    }
                    className="mt-3 w-full bg-shariti-cream border border-shariti-gray rounded-xl px-3 py-2 text-xs font-medium text-shariti-black outline-none focus:border-shariti-orange focus:ring-2 focus:ring-shariti-orange/10 resize-y"
                />
            )}

            {gatingFail && (
                <div className="mt-3 flex items-center gap-1.5 text-[11px] font-bold text-shariti-red">
                    <AlertOctagon className="h-3.5 w-3.5" />
                    Critère éliminatoire non rempli — l'entretien peut être écourté.
                </div>
            )}
        </motion.div>
    );
}
